import { Component, EventEmitter, Inject, InjectionToken, Input, OnDestroy, Optional, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { injectDestroyRef } from '@qpoint/utils';
import { cloneDeep, isEqual, pullAt } from 'lodash';
import { Subscription } from 'rxjs';

import { MapModeStateService } from '../map-mode-selection/map-mode-state.service';
import { QPointMapMode, QPointMapModeScope } from '../map-mode-selection/mode';

import PolygonOptions = google.maps.PolygonOptions;

export type QPointMapPolygonType = 'Polygon';

export interface QPointMapPolygon {
  type: QPointMapPolygonType;
  /**
   * WGS 84, Array of [Longitude, Latitude] tuples
   */
  coordinates: [number, number][][]; // WGS 84, [Longitude, Latitude]
  scope?: QPointMapModeScope;
}

export type QPointMapPolygonOptions = Partial<PolygonOptions> & {
  /**
   * provide custom label for the delete button inside the info window
   */
  deleteVertexLabel?: string;
  /**
   * provide custom label for the reset button inside the context window
   */
  resetPolygonLabel?: string;
  minimumVerticesNumber?: number;
};
export const QPOINT_POLYGON_DEFAULT_OPTIONS = new InjectionToken<QPointMapPolygonOptions>('Polygon Options');

@Component({
  selector: 'qpoint-map-polygon',
  template: '',
  standalone: true,
})
export class MapPolygonComponent implements OnDestroy {
  private destroyRef = injectDestroyRef();
  private readonly _defaultOptions: QPointMapPolygonOptions = this.defaultOptions || {
    fillColor: 'blue',
    fillOpacity: 0.15,
    strokeWeight: 0,
    zIndex: 10,
    deleteVertexLabel: 'maps.polygon.deleteVertex',
    resetPolygonLabel: 'maps.polygon.resetPolygon',
    minimumVerticesNumber: 4,
  };
  private editMode: boolean | undefined;
  private subscription: Subscription | null = null;
  private _scope = signal<QPointMapModeScope>('all');
  scope = this._scope.asReadonly();

  polygon: QPointMapPolygon | null = null;

  @Input('polygon') set polygonSetter(polygon: QPointMapPolygon) {
    this.polygon = cloneDeep(polygon);
    this._scope.set(polygon?.scope ?? 'all');
    this.setupSubscription();
  }

  @Input() options: QPointMapPolygonOptions;

  @Output() polygonChanged = new EventEmitter<QPointMapPolygon>();
  @Output() resetPolygonClicked = new EventEmitter<void>();

  private currentPolygonOptions: QPointMapPolygonOptions | null;

  constructor(@Inject(QPOINT_POLYGON_DEFAULT_OPTIONS) @Optional() private defaultOptions: QPointMapPolygonOptions,
    private mapModeState: MapModeStateService) {
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }

  get editable(): boolean {
    return this.editMode ?? this.options?.editable ?? this._defaultOptions?.editable ?? false;
  }

  get polygonOptions(): QPointMapPolygonOptions {
    const newOptions = this.createPolygonOptions();

    if (!this.currentPolygonOptions || !isEqual(this.currentPolygonOptions, newOptions)) {
      this.currentPolygonOptions = newOptions;
    }
    return this.currentPolygonOptions;
  }

  private createPolygonOptions(): QPointMapPolygonOptions {
    return {
      ...this._defaultOptions,
      ...this.options,
      editable: this.editable,
      zIndex: this.editable ? 100 : this.options?.zIndex ?? this._defaultOptions.zIndex,
      fillOpacity: this.mapModeState.anyModeActive() && !this.editable ? 0.07 : {
        ...this._defaultOptions,
        ...this.options
      }.fillOpacity
    };
  }

  canDeleteVertex(polygonIndex: number): boolean {
    return this.polygonOptions.editable && this.polygon.coordinates[polygonIndex]?.length > this.polygonOptions.minimumVerticesNumber;
  }

  deletePolygonVertex(event: google.maps.PolyMouseEvent, polygonIndex: number) {
    if (!this.canDeleteVertex(polygonIndex)) {
      return;
    }

    if (event.vertex === 0 || event.vertex === this.polygon.coordinates[polygonIndex].length - 1) {
      pullAt(this.polygon.coordinates[polygonIndex], [0, this.polygon.coordinates[polygonIndex].length - 1]);
      this.polygon.coordinates[polygonIndex].push(this.polygon.coordinates[polygonIndex][0]);
    } else {
      pullAt(this.polygon.coordinates[polygonIndex], event.vertex);
    }
    this.emitAndUpdatePolygon(this.polygon);
  }

  resetPolygon() {
    this.resetPolygonClicked.emit();
  }

  updatePolygon(polygon: QPointMapPolygon) {
    this.emitAndUpdatePolygon(polygon);
  }

  private emitAndUpdatePolygon(polygon: QPointMapPolygon) {
    this.polygon = cloneDeep(polygon);
    this.polygonChanged.emit(cloneDeep(polygon));
  }

  private setupSubscription() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.subscription = this.mapModeState.isModeActive$(QPointMapMode.Polygon, this.scope()).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: isActive => {
        this.editMode = isActive;
      }
    });
  }
}
